/**
 * Onboarding mutation client (LF-3216)
 *
 * `completeOnboardingAction` runs browser-side via `directApiClient`. After
 * a successful POST it pings `revalidateOnboardingStatusAction` (a tiny
 * server-only helper kept in `actions.ts`) to bust the `unstable_cache`
 * keyed on the user's token — without this, the dashboard layout would
 * keep returning the stale "incomplete" status until the 5s TTL expires.
 *
 * The read action `getOnboardingStatusAction` stays server-side: it's
 * consumed by `app/(dashboard)/layout.tsx` and `app/(dashboard)/onboarding/
 * page.tsx`, and the `unstable_cache` wrapper kills the prefetch/SSR-
 * navigation hammer.
 */

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import { revalidateOnboardingStatusAction } from './actions';
import type {
  GettingStartedStepKey,
  OnboardingActionResult,
  OnboardingSampleEmail
} from './types';

/** The sample client email the branding screen shows. See {@link OnboardingSampleEmail}. */
export async function fetchOnboardingSampleEmail(): Promise<OnboardingSampleEmail> {
  return directApiClient.get<OnboardingSampleEmail>('/onboarding/sample-email');
}

/**
 * Record that the user has done one Getting Started step.
 *
 * **Only `navigate` uses this.** Every other recorded step is written
 * server-side when the user's action actually commits, off the events that work
 * already emits — which is more truthful than anything the browser can assert,
 * and cannot record a step for work that then rolled back. Starting a guided
 * tour happens entirely in the browser and has no server-side action to hang
 * off, so it is reported (VALE-1830).
 *
 * **Never throws, and reports nothing to the user.** A checklist tick is worth
 * far less than whatever the user was actually doing, so a failure here is
 * swallowed — the step ticks next time. It also busts the cached status so the
 * hub and the sidebar ring reflect the new count without a reload.
 */
export async function recordGettingStartedStepAction(
  step: GettingStartedStepKey
): Promise<void> {
  try {
    await directApiClient.post(
      `/onboarding/getting-started/steps/${step}`,
      undefined
    );
    await revalidateOnboardingStatusAction();
  } catch {
    // Intentionally silent — see the doc comment above.
  }
}

export async function completeOnboardingAction(): Promise<
  OnboardingActionResult<void>
> {
  try {
    await directApiClient.post('/onboarding/complete', undefined);
    await revalidateOnboardingStatusAction();

    return { success: true, data: undefined };
  } catch (error) {
    if (error instanceof DirectApiError) {
      return { success: false, error: error.message };
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Request failed'
    };
  }
}

/**
 * How the reader leaves the first-sign-in prompt without finishing it
 * (VALE-1948). `remind-later` holds the prompt off for a week on the account;
 * `dismiss` stops it for good, and the backend refuses it while a detail the
 * reader's emails print is missing.
 */
export type OnboardingPromptDecision = 'remind-later' | 'dismiss';

export async function decideOnboardingPromptAction(
  decision: OnboardingPromptDecision
): Promise<OnboardingActionResult<void>> {
  try {
    await directApiClient.post('/onboarding/prompt-decision', { decision });
    await revalidateOnboardingStatusAction();

    return { success: true, data: undefined };
  } catch (error) {
    if (error instanceof DirectApiError) {
      return { success: false, error: error.message };
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Request failed'
    };
  }
}
